<script>
	const model = require('model/model');
	const pubsub = require('events/pubsub');

	export default {
		components: {
			'info-icon': require('components/svg/info-icon.vue').default
		},
		props: ['assessment', 'orderedStudents'],
		modelData: ['student'],
		methods: {
			navigateToStudentResult(uid) {
				pubsub.publish('hide-tooltips');
				model.set('student', uid);
			},
			vocabularyComplete(studentObject) {
				const vocabActivity = studentObject.vocabularyActivity || null;
				if (!vocabActivity) {
					return false;
				}

				if (Object.keys(vocabActivity.rtw.pre).length === 0) {
					return false;
				}

				if (Object.keys(vocabActivity.rtw.post).length === 0) {
					return false;
				}

				if (this.wordBuilderAssigned && !vocabActivity.word_builder) {
					return false;
				}

				if (this.wordChangerAssigned && !vocabActivity.word_changer) {
					return false;
				}

				if (this.wordMatcherAssigned && !vocabActivity.word_matcher) {
					return false;
				}

				return true;
			},
			ratedWords(student) {
				const words = [];
				const vocabActivity = student.vocabularyActivity || null;
				if (!vocabActivity) {
					return [];
				}

				const wordsByKey = {};
				[vocabActivity.rtw.pre, vocabActivity.rtw.post].forEach(rtw => {
					Object.keys(rtw).forEach(w => {
						if (!wordsByKey[w]) {
							wordsByKey[w] = {
								title: w,
								avg: false
							};
							words.push(wordsByKey[w]);
						}
					});
				});

				function average(arr) {
					if (!arr.length) {
						return null;
					}
					let avg = arr.reduce((tot, i) => i + tot, 0) / arr.length;
					if (avg !== Math.round(avg)) {
						avg = avg.toFixed(2);
					}

					return '' + avg;
				}

				const preRatings = [];
				Object.keys(vocabActivity.rtw.pre).forEach(w => {
					const rating = vocabActivity.rtw.pre[w];
					wordsByKey[w].pre = null;
					if (typeof rating === 'number') {
						preRatings.push(rating);
						wordsByKey[w].pre = rating;
					}
				});

				const postRatings = [];
				const deltas = [];
				Object.keys(vocabActivity.rtw.post).forEach(w => {
					const rating = vocabActivity.rtw.post[w];
					wordsByKey[w].post = null;
					if (typeof rating === 'number') {
						postRatings.push(rating);
						wordsByKey[w].post = rating;
					}

					if (typeof wordsByKey[w].post === 'number' && typeof wordsByKey[w].pre === 'number') {
						wordsByKey[w].delta = wordsByKey[w].post - wordsByKey[w].pre;
						deltas.push(wordsByKey[w].delta);
					} else {
						wordsByKey[w].delta = null;
					}
				});

				const avg = {
					avg: true,
					title: 'Average',
					pre: average(preRatings),
					post: average(postRatings),
					delta: average(deltas),
					totalDelta: deltas.reduce((tot, i) => i + tot, 0)
				};

				words.sort((a, b) => {
					if (a.word < b.word) {
						return -1;
					}
					if (a.word > b.word) {
						return 1;
					}
					return 0;
				});
				return [avg].concat(words);
			}
		},
		computed: {
			currentStudent() {
				return this.students.filter(s => s.uid === this.student)[0] || null;
			},
			wordBuilderAssigned() {
				return this.assessment && this.assessment.options.va && this.assessment.options.va.indexOf('word_builder') !== -1;
			},
			wordChangerAssigned() {
				return this.assessment && this.assessment.options.va && this.assessment.options.va.indexOf('word_changer') !== -1;
			},
			wordMatcherAssigned() {
				return this.assessment && this.assessment.options.va && this.assessment.options.va.indexOf('word_matcher') !== -1;
			},
			submittedStudentCount() {
				let count = 0;
				this.orderedStudents.forEach(student => {
					if (student.submitted) {
						count += 1;
					}
				});
				return count;
			},
			students() {
				return this.orderedStudents.map(student => {
					return $.extend({}, student, {
						ratedWords: this.ratedWords(student)
					});
				});
			}
		}
	};
</script>
<template>
	<section class="wrapper class-results vocab-results">
		<section>
			<template v-if="student">
				Activity:
				<span
					class="complete"
					v-if="vocabularyComplete(currentStudent)">
					Complete
				</span>
				<span
					class="incomplete"
					v-else>
					Incomplete
				</span>

				<table
					class="results"
					v-if="currentStudent.ratedWords.length > 0">
					<thead>
						<tr>
							<th class="word-col">Word</th>
							<th>Initial Rating</th>
							<th>Final Rating</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="rec in currentStudent.ratedWords"
							:key="rec.title"
							:class="{ 'average-row': rec.avg, 'word-row': !rec.avg }">
							<td class="word-col">{{ rec.title }}</td>
							<td class="rating-col">{{ rec.pre }}</td>
							<td class="rating-col">
								{{ rec.post }}
								<template v-if="typeof rec.delta === 'number'">
									(<span
										:class="{
											'positive-delta': parseFloat(rec.delta) >= 0,
											'negative-delta': parseFloat(rec.delta) < 0
										}"
										>{{ rec.delta }}</span
									>)
								</template>
							</td>
						</tr>
					</tbody>
				</table>
			</template>
			<table
				v-else
				class="results">
				<thead>
					<tr>
						<th class="student-name-col">Students</th>
						<th colspan="2">Vocabulary Activity</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td class="submitted-total">{{ submittedStudentCount }}/{{ orderedStudents.length }} Submitted</td>
						<td></td>
						<td class="sub-header">
							Change
							<button
								class="button -link"
								v-tooltip="'This is the change in your students\' rating from the first to the second time they rated their familiarity with the words.'">
								<info-icon></info-icon>
							</button>
						</td>
					</tr>
					<tr
						v-for="student in students"
						:key="student.uid">
						<td class="student-name-col">
							<div class="student-name-container">
								<a
									v-if="student.hasWorkspace"
									class="student-name print-visible"
									href="javascript:void(0)"
									data-tab="studentresults"
									v-on:click.prevent="navigateToStudentResult(student.uid)"
									v-tooltip="'View student vocabulary results'">
									{{ student.firstName }} {{ student.lastName }}
								</a>
								<span
									v-else
									class="student-name">
									{{ student.firstName }} {{ student.lastName }}
								</span>
								<overdue-icon v-if="student.overdue"></overdue-icon>
							</div>
						</td>
						<td class="status-col">
							<span
								class="complete"
								v-if="vocabularyComplete(student)">
								Complete
							</span>
							<span
								class="incomplete"
								v-else>
								Incomplete
							</span>
						</td>
						<td class="status-col">
							<template v-if="vocabularyComplete(student)">
								<span
									:class="{
										'positive-delta': student.ratedWords[0].totalDelta >= 0,
										'negative-delta': student.ratedWords[0].totalDelta < 0
									}"
									>{{ student.ratedWords[0].totalDelta }}</span
								>
							</template>
						</td>
					</tr>
				</tbody>
			</table>
		</section>
	</section>
</template>
<style lang="scss">
	@import 'config/colors';

	.vocab-results {
		table.results {
			width: auto;
			margin-top: 0.5rem;
			.status-col,
			.rating-col,
			th,
			.sub-header {
				text-align: center;
			}
			.sub-header {
				font-weight: bold;
			}
			.student-name-col {
				width: 17rem;
			}
			.rating-col,
			.status-col {
				width: 13rem;
			}
			.word-col {
				width: 15rem;
			}
			.submitted-total {
				font-weight: bold;
			}
			.average-row {
				font-weight: bold;
			}
		}
		.complete,
		.positive-delta {
			color: $c-green;
		}
		.negative-delta {
			color: $c-red;
		}
		.positive-delta::before {
			content: '+';
		}
		.incomplete {
			color: $c-slate-dark;
		}
	}
</style>
